import { DAY_VERSION, PROFILE_VERSION, defaultAppearance, newProfile, type DayState, type Meta, type Profile } from './state';

/**
 * 存檔（localStorage）。所有讀寫都包 try/catch：無痕模式或封鎖儲存時遊戲照常可玩，只是不存。
 * 版本升級時在 MIGRATIONS 新增轉換函式，舊存檔會自動升級，不會因改版而壞檔。
 */
const KEY = { profile: 'ffg:profile', day: 'ffg:day', meta: 'ffg:meta' } as const;

type Migration = (x: any) => any;
const PROFILE_MIGRATIONS: Record<number, Migration> = {
  // v1（V2 遊戲）→ v2（V3）：補上成就、旗標、外觀等新欄位，保留天數與圖鑑
  1: (p) => ({ ...newProfile(), ...p, version: 2, counters: {}, flags: {}, achievements: {}, challenges: [], productsSeen: [], knowledge: [], cadresTalked: [], deptsPlayed: [], appearance: defaultAppearance(), codes: {} }),
  // v2（遊戲 v0.3）→ v3（v0.4）：任務系統改版，補上已完成的一次性任務清單；舊進度代碼的當班快照不相容，清掉代碼清單
  2: (p) => ({ ...p, version: 3, missionsDone: [], codes: {}, settings: { ...p.settings, guide: true } }),
  // v3（v0.4）→ v4（v0.5）：任務欄可以開闔（預設打開）
  3: (p) => ({ ...p, version: 4, settings: { ...p.settings, tasksOpen: true } }),
  // v4（v0.5）→ v5（v0.6）：經驗、等級、薪水、福利點、食譜收集、小知識
  4: (p) => ({ ...p, version: 5, xp: 0, points: 0, salary: 29500, raises: [], stash: {}, recipes: [], tipsHeard: [], streak: 0 }),
};
// 當班存檔：v0.3 → v0.4 地圖、任務都重做，不遷移（讀到就作廢，Profile 保留）。
// v0.4 → v0.5：補上背包與「走過氣浴室」狀態；站的位置如果變成牆，載入時會自動移到最近的空地。
const DAY_MIGRATIONS: Record<number, Migration> = {
  3: (d) => ({ ...d, version: 4, pack: [], inProd: !!d.washed }),
  // v0.5 → v0.6：地圖重排（舊位置如果卡在牆裡，載入時會移到最近的空地）、今天的收穫、老鼠、貨架
  4: (d) => ({ ...d, version: 5, earn: { xp: 0, points: 0, raise: 0, notes: [] }, rat: null, shelfTaken: {}, chatted: [] }),
};

function migrate<T extends { version: number }>(obj: any, target: number, table: Record<number, Migration>): T | null {
  if (!obj || typeof obj.version !== 'number') return null;
  let cur = obj;
  while (cur.version < target) {
    const m = table[cur.version];
    if (!m) return null;           // 沒有遷移路徑就丟棄（避免壞檔卡死）
    cur = m(cur);
  }
  return cur.version === target ? cur as T : null;
}

function read(key: string): any {
  try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : null; } catch { return null; }
}
function write(key: string, val: unknown): void {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* 儲存不可用時靜默 */ }
}
function remove(key: string): void { try { localStorage.removeItem(key); } catch { /* noop */ } }

export const Save = {
  loadProfile(): Profile { return migrate<Profile>(read(KEY.profile), PROFILE_VERSION, PROFILE_MIGRATIONS) ?? newProfile(); },
  saveProfile(p: Profile) { write(KEY.profile, p); },
  loadDay(): { day: DayState; meta: Meta } | null {
    const d = migrate<DayState>(read(KEY.day), DAY_VERSION, DAY_MIGRATIONS);
    if (!d) return null;
    const meta = read(KEY.meta) as Meta | null;
    return { day: d, meta: meta && Array.isArray(meta.checkpoints) ? meta : { rewinds: 0, checkpoints: [] } };
  },
  saveDay(d: DayState, m: Meta) { write(KEY.day, d); write(KEY.meta, { rewinds: m.rewinds, checkpoints: m.checkpoints.slice(-4) }); },
  /** 進度代碼的本機快照（同一台瀏覽器輸入短代碼即可回到該點） */
  saveSnapshot(code: string, d: DayState) { write(`ffg:code:${code}`, d); },
  loadSnapshot(code: string): DayState | null { return migrate<DayState>(read(`ffg:code:${code}`), DAY_VERSION, DAY_MIGRATIONS); },
  clearDay() { remove(KEY.day); remove(KEY.meta); },
  resetAll() {
    remove(KEY.profile); remove(KEY.day); remove(KEY.meta);
    try { Object.keys(localStorage).filter(k => k.startsWith('ffg:code:')).forEach(k => localStorage.removeItem(k)); } catch { /* noop */ }
  },
};
